import { sessionHeaders } from '../../api';
export class FeedbackApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'FeedbackApiError';
    this.code = code;
    this.status = status;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  Object.assign(headers, sessionHeaders());

  const res = await fetch(path, {
    method,
    headers,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!res.ok) {
    throw new FeedbackApiError(
      String(payload['error'] ?? 'E00.internal'),
      String(payload['message'] ?? 'Ошибка запроса'),
      res.status,
    );
  }
  return payload['data'] as T;
}

export type FeedbackStatus = 'new' | 'assigned' | 'answered' | 'escalated' | 'closed';

export interface FeedbackItem {
  readonly id: string;
  readonly siteId: string;
  readonly visitId: string | null;
  readonly customerId: string | null;
  readonly employeeId: string | null;
  readonly assigneeId: string | null;
  readonly channel: string;
  readonly score: number | null;
  readonly body: string | null;
  readonly category: string | null;
  readonly categorySource: 'none' | 'suggested' | 'confirmed';
  readonly reasonCode: string | null;
  readonly status: FeedbackStatus;
  readonly dueAt: string | null;
  readonly answeredAt: string | null;
  readonly closedAt: string | null;
  readonly resolution: string | null;
  readonly createdAt: string;
  readonly overdue: boolean;
  /**
   * Куда из текущего статуса вообще можно перейти — по таблице переходов сервера.
   * Без этого интерфейс разблокировал «Закрыть» из статуса «Эскалировано», где
   * сервер отвечает 409: закрыть можно только из «Отвечено».
   */
  readonly allowedTransitions?: readonly FeedbackStatus[];
  /** Телефон для ответа: полностью — тем, кто вправе видеть ПДн, иначе маской. */
  readonly contactPhone?: string | null;
  /** Эскалации по обращению; приходят только с карточкой. */
  readonly escalations?: readonly EscalationItem[];
}

export interface QueueFilters {
  /** Выбранный в оболочке объект. Пусто — все доступные роли. */
  readonly siteId?: string;
  readonly status?: string;
  readonly channel?: string;
  readonly category?: string;
  readonly scoreMax?: string;
  readonly overdue?: boolean;
  readonly unlinked?: boolean;
}

export function listFeedback(filters: QueueFilters): Promise<{
  items: readonly FeedbackItem[];
  nextCursor: string | null;
}> {
  const p = new URLSearchParams();
  if (filters.siteId) p.set('site_id', filters.siteId);
  if (filters.status) p.set('status', filters.status);
  if (filters.channel) p.set('channel', filters.channel);
  if (filters.category) p.set('category', filters.category);
  if (filters.scoreMax) p.set('score_max', filters.scoreMax);
  if (filters.overdue) p.set('overdue', 'true');
  if (filters.unlinked) p.set('unlinked', 'true');
  const qs = p.toString();
  return request('GET', `/api/v1/feedback${qs ? `?${qs}` : ''}`);
}

export function getFeedback(id: string): Promise<FeedbackItem> {
  return request('GET', `/api/v1/feedback/${encodeURIComponent(id)}`);
}

export function assignFeedback(id: string, assigneeId: string): Promise<FeedbackItem> {
  return request('POST', `/api/v1/feedback/${encodeURIComponent(id)}/assign`, { assigneeId });
}

export function answerFeedback(id: string, text: string): Promise<FeedbackItem> {
  return request('POST', `/api/v1/feedback/${encodeURIComponent(id)}/answer`, { text });
}

export function categorizeFeedback(
  id: string,
  category: string,
  reason: string | null,
): Promise<FeedbackItem> {
  return request('POST', `/api/v1/feedback/${encodeURIComponent(id)}/categorize`, {
    category,
    reason,
  });
}

export function closeFeedback(id: string, resolution: string): Promise<FeedbackItem> {
  return request('POST', `/api/v1/feedback/${encodeURIComponent(id)}/close`, { resolution });
}

export function escalateFeedback(id: string, note: string): Promise<FeedbackItem> {
  return request('POST', `/api/v1/feedback/${encodeURIComponent(id)}/escalate`, { note });
}

export interface CategoryCatalog {
  readonly categories: ReadonlyArray<{ code: string; nameRu: string; nameKk: string }>;
  readonly reasons: ReadonlyArray<{
    categoryCode: string;
    code: string;
    nameRu: string;
    nameKk: string;
  }>;
}

export function categoryCatalog(): Promise<CategoryCatalog> {
  return request('GET', '/api/v1/feedback/categories');
}

export interface SlaRow {
  readonly siteId: string | null;
  readonly siteName: string | null;
  readonly total: number;
  readonly closed: number;
  readonly withinTarget: number;
  readonly sharePercent: number | null;
  readonly shareOfClosedPercent: number | null;
  readonly avgResponseMinutes: number | null;
  readonly openOverdue: number;
}

export interface SlaReport {
  readonly targetHours: number;
  /** Формула печатается рядом с числом: иначе спор «как ты считал» неизбежен. */
  readonly formula: string;
  readonly overall: SlaRow;
  readonly bySite: readonly SlaRow[];
}

export function slaReport(): Promise<SlaReport> {
  return request('GET', '/api/v1/feedback/reports/sla');
}

export interface CategoryReport {
  readonly categories: ReadonlyArray<{
    category: string | null;
    reasonCode: string | null;
    total: number;
    avgScore: number | null;
  }>;
  readonly scores: ReadonlyArray<{ score: number; total: number }>;
  readonly employees: ReadonlyArray<{
    employeeId: string;
    employeeName: string | null;
    total: number;
    avgScore: number | null;
    lowScore: number;
  }>;
  readonly unconfirmed: number;
  readonly unlinkedVisits: number;
}

export function categoryReportApi(): Promise<CategoryReport> {
  return request('GET', '/api/v1/feedback/reports/categories');
}

export function siteQr(siteId: string): Promise<{ siteId: string; token: string; formPath: string }> {
  return request('GET', `/api/v1/feedback/qr/${encodeURIComponent(siteId)}`);
}

export function runTick(): Promise<{ reminded: number; overdue: number }> {
  return request('POST', '/api/v1/feedback/tick');
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  Object.assign(headers, sessionHeaders());
  return headers;
}

/**
 * Картинка QR-кода. Отдаётся файлом, поэтому читается не как JSON, а как Blob;
 * имя файла сервер кладёт в content-disposition, ссылку — в x-qr-url.
 */
export async function qrImage(
  path: string,
  format: 'svg' | 'png',
): Promise<{ blob: Blob; filename: string; url: string }> {
  const res = await fetch(`${path}?format=${format}`, { headers: authHeaders() });
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new FeedbackApiError(
      String(payload['error'] ?? 'E00.internal'),
      String(payload['message'] ?? 'Ошибка запроса'),
      res.status,
    );
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/.exec(disposition)?.[1];
  return {
    blob: await res.blob(),
    filename: encoded ? decodeURIComponent(encoded) : `moika-qr.${format}`,
    url: res.headers.get('x-qr-url') ?? '',
  };
}

export interface SiteQr {
  readonly siteId: string;
  readonly token: string;
  readonly formPath: string;
  readonly formUrl: string;
  readonly imagePath: string;
}

export function siteQrInfo(siteId: string): Promise<SiteQr> {
  return request('GET', `/api/v1/feedback/qr/${encodeURIComponent(siteId)}`);
}

export interface FeedbackPoint {
  readonly id: string;
  readonly siteId: string;
  readonly siteName: string | null;
  readonly name: string;
  readonly token: string;
  readonly status: 'active' | 'archived';
  readonly feedbackCount: number;
  readonly formUrl: string;
}

export function listPoints(siteId: string): Promise<{ items: readonly FeedbackPoint[] }> {
  return request('GET', `/api/v1/feedback/points?site_id=${encodeURIComponent(siteId)}`);
}

export function createPoint(siteId: string, name: string): Promise<FeedbackPoint> {
  return request('POST', '/api/v1/feedback/points', { siteId, name });
}

export function updatePoint(
  id: string,
  patch: { name?: string; status?: 'active' | 'archived'; rotateToken?: boolean },
): Promise<FeedbackPoint> {
  return request('PATCH', `/api/v1/feedback/points/${encodeURIComponent(id)}`, patch);
}

export interface EscalationItem {
  readonly id: string;
  readonly feedbackId: string;
  readonly reason: 'low_score' | 'overdue' | 'manual';
  readonly note: string | null;
  readonly toEmployeeId: string | null;
  readonly toEmployeeName: string | null;
  readonly createdAt: string;
  readonly siteId: string;
  readonly siteName: string | null;
  readonly score: number | null;
  readonly channel: string;
  readonly status: FeedbackStatus;
  readonly excerpt: string | null;
  readonly open: boolean;
}

export function listEscalations(filters: {
  siteId?: string;
  openOnly?: boolean;
  reason?: string;
}): Promise<{ items: readonly EscalationItem[] }> {
  const p = new URLSearchParams();
  if (filters.siteId) p.set('site_id', filters.siteId);
  if (filters.openOnly) p.set('open', 'true');
  if (filters.reason) p.set('reason', filters.reason);
  const qs = p.toString();
  return request('GET', `/api/v1/feedback/escalations${qs ? `?${qs}` : ''}`);
}

export interface NotificationItem {
  readonly id: string;
  readonly channel: string;
  readonly templateCode: string | null;
  readonly status: string;
  readonly employeeName: string | null;
  readonly customerName: string | null;
  readonly recipientMasked: string;
  readonly suppressedReason: string | null;
  readonly lastError: string | null;
  readonly createdAt: string;
  readonly sentAt: string | null;
}

/** Сообщения, отправленные по обращению: ответ клиенту и уведомления руководителю. */
export function feedbackNotifications(feedbackId: string): Promise<{ items: readonly NotificationItem[] }> {
  return request('GET', `/api/v1/comms/log?entity_type=feedback&entity_id=${encodeURIComponent(feedbackId)}`);
}

export interface SlaPolicyRow {
  readonly id: string;
  readonly siteId: string | null;
  readonly siteName: string | null;
  readonly responseMinutes: number;
  readonly remindBeforeMinutes: number;
  readonly lowScoreThreshold: number;
  readonly visitWindowMinutes: number;
  readonly publicWindowMinutes: number;
  readonly publicMaxPerWindow: number;
  readonly updatedAt: string;
}

export type PolicyInput = Omit<SlaPolicyRow, 'id' | 'siteName' | 'updatedAt'>;

export function listPolicies(): Promise<{ items: readonly SlaPolicyRow[] }> {
  return request('GET', '/api/v1/feedback/settings/policy');
}

export function savePolicy(input: PolicyInput): Promise<SlaPolicyRow> {
  return request('PUT', '/api/v1/feedback/settings/policy', input);
}

export function resetSitePolicy(siteId: string): Promise<{ removed: boolean }> {
  return request('DELETE', `/api/v1/feedback/settings/policy/${encodeURIComponent(siteId)}`);
}

export type RoutingRole = 'site_manager' | 'marketer' | 'network_admin';

export interface RoutingRule {
  readonly id: string;
  readonly name: string;
  readonly siteId: string | null;
  readonly siteName: string | null;
  readonly categoryCode: string | null;
  readonly scoreMax: number | null;
  readonly channel: string | null;
  readonly assigneeId: string | null;
  readonly assigneeName: string | null;
  readonly assigneeRole: RoutingRole | null;
  readonly escalateToId: string | null;
  readonly escalateToName: string | null;
  readonly escalateToRole: RoutingRole | null;
  readonly responseMinutes: number | null;
  readonly priority: number;
  readonly status: 'active' | 'archived';
}

export type RoutingRuleInput = Omit<RoutingRule, 'id' | 'siteName' | 'assigneeName' | 'escalateToName'>;

export function listRoutingRules(): Promise<{ items: readonly RoutingRule[] }> {
  return request('GET', '/api/v1/feedback/settings/routing');
}

export function createRoutingRule(input: RoutingRuleInput): Promise<RoutingRule> {
  return request('POST', '/api/v1/feedback/settings/routing', input);
}

export function updateRoutingRule(id: string, input: RoutingRuleInput): Promise<RoutingRule> {
  return request('PUT', `/api/v1/feedback/settings/routing/${encodeURIComponent(id)}`, input);
}

export interface FeedbackSummary {
  readonly from: string;
  readonly to: string;
  readonly method: 'extractive';
  readonly text: string;
  readonly paragraphs: readonly string[];
  readonly stats: { readonly total: number; readonly lowScore: number; readonly previousTotal: number };
}

export function feedbackSummary(input: {
  from: string;
  to: string;
  siteId?: string;
  locale: string;
}): Promise<FeedbackSummary> {
  const p = new URLSearchParams({ from: input.from, to: input.to, locale: input.locale });
  if (input.siteId) p.set('site_id', input.siteId);
  return request('GET', `/api/v1/feedback/reports/summary?${p.toString()}`);
}

export interface ArchiveItem {
  readonly id: string;
  readonly source: string;
  readonly sourceRef: string;
  readonly receivedAt: string | null;
  readonly channel: string | null;
  readonly subject: string | null;
  readonly body: string;
  readonly resolution: string | null;
}

export interface ArchivePage {
  readonly items: readonly ArchiveItem[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
  readonly channels: readonly string[];
}

export function feedbackArchive(filters: {
  q?: string;
  channel?: string;
  offset: number;
  limit: number;
}): Promise<ArchivePage> {
  const p = new URLSearchParams({ offset: String(filters.offset), limit: String(filters.limit) });
  if (filters.q) p.set('q', filters.q);
  if (filters.channel) p.set('channel', filters.channel);
  return request('GET', `/api/v1/bridge/feedback-archive?${p.toString()}`);
}
